import React, { useState, useEffect } from "react";
import {
  TextInput,
  Select,
  Checkbox,
  Button,
  Stack,
  Group,
  Text,
  Box,
  Grid,
  Alert,
  Divider,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import StandardModal from "../shared/Modal/StandardModal";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./EditProjectedRevenueModal.css";

// Mirrors the iOS EditRepContactSheet: Contact / Social / Details /
// Organization sections, and a minimal-diff save — only the keys whose values
// actually changed from the loaded rep are sent to PUT /v1/rep/details/:rep_id.
// Web-only extras kept here: name editing at the top, and Is Manager + Rep Rank
// in the Organization section.

// A related rep can arrive as a nested object ({user: {rep_id}} or {rep_id})
// or as a bare id — normalize all shapes to a select value string.
const relatedRepId = value => {
  if (value == null) return "";
  if (typeof value === "object") {
    const id = value.user?.rep_id ?? value.rep_id;
    return id != null ? String(id) : "";
  }
  return String(value);
};

// The rep's loaded values, in form shape — both the form seed and the baseline
// the save diff compares against.
const initialValuesFor = rep => ({
  firstName: rep.first_name || "",
  lastName: rep.last_name || "",
  email: rep.email || "",
  phoneNumber: rep.phone_number || "",
  instagram: rep.instagram || "",
  linkedin: rep.linked_in || "",
  joined: rep.sale_start_date ? rep.sale_start_date.split("T")[0] : "",
  birthday: rep.birthday ? rep.birthday.split("T")[0] : "",
  manager: relatedRepId(rep.manager),
  recruitedBy: relatedRepId(rep.recruiter) || relatedRepId(rep.recruited_by),
  recruitingSource: rep.recruiting_source_id
    ? String(rep.recruiting_source_id)
    : "",
  isManager: rep.is_manager || false,
  repRank: rep.rep_type_id ? String(rep.rep_type_id) : "",
});

function EditRepInfoModal({
  isOpen,
  onClose,
  rep,
  reps = [],
  repTypes = [],
  recruitingSourceTypes = [],
  onUpdate,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [initialValues, setInitialValues] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    instagram: "",
    linkedin: "",
    joined: "",
    birthday: "",
    manager: "",
    recruitedBy: "",
    recruitingSource: "",
    isManager: false,
    repRank: "",
  });

  // Initialize form data when modal opens or rep changes
  useEffect(() => {
    if (rep && isOpen) {
      const values = initialValuesFor(rep);
      setInitialValues(values);
      setFormData(values);
      setError(null);
    }
  }, [rep, isOpen]);

  const setField = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // Build the minimal changed-fields patch, mirroring the iOS save rules:
  // trimmed strings, email lowercased and never emptied, manager never sent as
  // null, recruited_by / recruiting_source_id nullable to clear, dates as
  // yyyy-MM-dd. Returns null when a validation error was set.
  const buildChangedFields = () => {
    const init = initialValues || {};
    const fields = {};
    const trim = value => (value || "").trim();

    if (trim(formData.firstName) !== init.firstName) {
      fields.first_name = trim(formData.firstName);
    }
    if (trim(formData.lastName) !== init.lastName) {
      fields.last_name = trim(formData.lastName);
    }

    const email = trim(formData.email).toLowerCase();
    if (email !== (init.email || "").toLowerCase()) {
      if (!email) {
        setError("Email can't be empty.");
        return null;
      }
      fields.email = email;
    }

    const phone = trim(formData.phoneNumber);
    if (phone !== init.phoneNumber) fields.phone_number = phone;

    const instagram = trim(formData.instagram);
    if (instagram !== init.instagram) fields.instagram_username = instagram;

    const linkedin = trim(formData.linkedin);
    if (linkedin !== init.linkedin) fields.linkedin_profile = linkedin;

    if (formData.joined && formData.joined !== init.joined) {
      fields.sale_start_date = formData.joined;
    }
    if (formData.birthday && formData.birthday !== init.birthday) {
      fields.birthday = formData.birthday;
    }

    // Manager can only be set to a valid rep — never cleared to null here.
    if (formData.manager !== init.manager && formData.manager) {
      fields.manager = parseInt(formData.manager);
    }
    if (formData.recruitedBy !== init.recruitedBy) {
      fields.recruited_by = formData.recruitedBy
        ? parseInt(formData.recruitedBy)
        : null;
    }
    if (formData.recruitingSource !== init.recruitingSource) {
      fields.recruiting_source_id = formData.recruitingSource
        ? parseInt(formData.recruitingSource)
        : null;
    }

    if (formData.isManager !== init.isManager) {
      fields.is_manager = formData.isManager;
    }
    if (formData.repRank !== init.repRank && formData.repRank) {
      fields.rep_type_id = parseInt(formData.repRank);
    }

    return fields;
  };

  const handleSave = async () => {
    setError(null);
    const fields = buildChangedFields();
    if (fields === null) return;

    // Nothing changed — just close, no request.
    if (Object.keys(fields).length === 0) {
      onClose();
      return;
    }

    try {
      setLoading(true);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v1/rep/details/${rep.rep_id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(fields),
        }
      );

      if (response.ok) {
        // Trigger parent component to refetch fresh data from API
        onUpdate();
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || "Failed to update rep information");
      }
    } catch (err) {
      setError("Failed to update rep information");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (initialValues) setFormData(initialValues);
    setError(null);
    onClose();
  };

  // Managers only for the Manager select; every rep for Recruited By.
  const managerData = [
    { value: "", label: "Select…" },
    ...reps
      .filter(r => r.is_manager)
      .map(r => ({
        value: String(r.rep_id),
        label: `${r.first_name} ${r.last_name}`,
      })),
  ];

  const recruiterData = [
    { value: "", label: "None" },
    ...reps.map(r => ({
      value: String(r.rep_id),
      label: `${r.first_name} ${r.last_name}`,
    })),
  ];

  const recruitingSourceData = [
    { value: "", label: "None" },
    ...recruitingSourceTypes.map(sourceType => ({
      value: String(sourceType.id),
      label: sourceType.source_name,
    })),
  ];

  const repTypeData = [
    { value: "", label: "Select rep rank..." },
    ...repTypes.map(repType => ({
      value: String(repType.id),
      label: repType.name,
    })),
  ];

  const sectionLabel = label => (
    <Text size="sm" fw={600} c="dimmed" tt="uppercase">
      {label}
    </Text>
  );

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit Contact"
      footerLeft={<div></div>}
      footerRight={
        <Group justify="flex-end">
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading}>
            Save
          </Button>
        </Group>
      }
    >
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          mb="md"
        >
          {error}
        </Alert>
      )}
      <Stack gap="lg">
        {/* Name — web-only; iOS edits the rep's name elsewhere. */}
        <Box>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="First Name"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={e => setField("firstName", e.target.value)}
                disabled={loading}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Last Name"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={e => setField("lastName", e.target.value)}
                disabled={loading}
                required
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Contact */}
        <Box>
          {sectionLabel("Contact")}
          <Divider mt={4} mb="md" />
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Mobile"
                placeholder="Phone number"
                type="tel"
                value={formData.phoneNumber}
                onChange={e => setField("phoneNumber", e.target.value)}
                disabled={loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Email"
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={e => setField("email", e.target.value)}
                disabled={loading}
                required
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Social */}
        <Box>
          {sectionLabel("Social")}
          <Divider mt={4} mb="md" />
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Instagram"
                placeholder="@handle"
                value={formData.instagram}
                onChange={e => setField("instagram", e.target.value)}
                disabled={loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="LinkedIn"
                placeholder="Profile URL or handle"
                value={formData.linkedin}
                onChange={e => setField("linkedin", e.target.value)}
                disabled={loading}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Details */}
        <Box>
          {sectionLabel("Details")}
          <Divider mt={4} mb="md" />
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="Joined"
                value={formData.joined}
                onChange={e => setField("joined", e.target.value)}
                disabled={loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="Birthday"
                value={formData.birthday}
                onChange={e => setField("birthday", e.target.value)}
                disabled={loading}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Organization */}
        <Box>
          {sectionLabel("Organization")}
          <Divider mt={4} mb="md" />
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Manager"
                value={formData.manager}
                onChange={value => setField("manager", value || "")}
                data={managerData}
                disabled={loading}
                searchable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Recruited By"
                value={formData.recruitedBy}
                onChange={value => setField("recruitedBy", value || "")}
                data={recruiterData}
                disabled={loading}
                searchable
              />
            </Grid.Col>
          </Grid>
          <Grid mt="md">
            <Grid.Col span={6}>
              <Select
                label="Source"
                value={formData.recruitingSource}
                onChange={value => setField("recruitingSource", value || "")}
                data={recruitingSourceData}
                disabled={loading}
                searchable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Rep Rank"
                value={formData.repRank}
                onChange={value => setField("repRank", value || "")}
                data={repTypeData}
                disabled={loading}
                searchable
              />
            </Grid.Col>
          </Grid>
          <Checkbox
            label="Is Manager?"
            checked={formData.isManager}
            onChange={e => setField("isManager", e.currentTarget.checked)}
            disabled={loading}
            mt="md"
          />
        </Box>
      </Stack>
    </StandardModal>
  );
}

export default EditRepInfoModal;
